import type { TextSearchMatch, WebContainer } from '@webcontainer/api';
import { atom, computed, map } from 'nanostores';
import { workbenchStore } from '~/lib/stores/workbench';
import { WORK_DIR } from '~/utils/constants';
import { debounce } from '~/utils/debounce';
import { hmrPropertyFactory } from '~/utils/hmr';
import { logger } from '~/utils/logger';
import { watchAll } from '~/utils/nanostore';

const hmr = hmrPropertyFactory('SearchStore');

export class SearchStore {
  #webcontainer: Promise<WebContainer>;

  searchQuery = atom<string>('');

  /**
   * Whether to use a regular expression for the search query.
   */
  @hmr()
  isRegex = atom<boolean>(false);

  /**
   * Whether to match the case of the search query.
   */
  @hmr()
  caseSensitive = atom<boolean>(false);

  /**
   * Map of file paths to their search results.
   */
  @hmr()
  results = map<Record<string, TextSearchMatch[]>>({});

  /**
   * Number of characters visible before truncation in the search results.
   */
  @hmr()
  renderedCharCount = atom<number>(0);

  /**
   * Whether a search is currently in progress.
   */
  @hmr()
  isSearching = atom<boolean>(false);

  @hmr()
  searchError = atom<string | null>(null);

  #excludedFromSearch = [
    'node_modules',
    'dist',
    '.next',
    '.nuxt',
    '.astro',
    '.contentlayer',
    '.svelte-kit',
    '.vitepress',
    '.vscode',
    '.stackblitz',
    '.bolt',
    '.codesandbox',
    'package-lock.json',
    'yarn.lock',
    'pnpm-lock.yaml',
    'bun.lockb',
  ];

  constructor(webcontainerPromise: Promise<WebContainer>) {
    this.#webcontainer = webcontainerPromise;

    watchAll(this.searchQuery, this.isRegex, this.caseSensitive).subscribe(() => {
      if (this.regExpError.get() === null) {
        this.performSearch();
      }
    });
  }

  resultsArray = computed([this.results], (results) => {
    return Object.entries(results).map(([path, matches]) => ({
      path,
      matches,
    }));
  });

  setSearchQuery = debounce((query: string) => {
    this.searchQuery.set(query);
  }, 100);

  regExpError = computed([this.searchQuery, this.isRegex], (searchQuery, isRegex) => {
    if (!isRegex) {
      return null;
    }

    try {
      new RegExp(searchQuery);
      return null;
    } catch (error) {
      return (error as Error).message;
    }
  });

  error = computed([this.searchError, this.regExpError], (searchError, regExpError) => {
    return searchError || regExpError;
  });

  showNoResults = computed([this.results, this.isSearching], (results, isPending) => {
    return !isPending && Object.keys(results).length === 0 && this.searchQuery.get().length > 0;
  });

  toggleRegex() {
    this.isRegex.set(!this.isRegex.get());
  }

  toggleCaseSensitivity() {
    this.caseSensitive.set(!this.caseSensitive.get());
  }

  async performSearch() {
    const searchQuery = this.searchQuery.get();
    const isRegex = this.isRegex.get();
    const caseSensitive = this.caseSensitive.get();

    this.searchError.set(null);
    this.results.set({});

    if (!searchQuery) {
      this.isSearching.set(false);
      return;
    }

    this.isSearching.set(true);

    const webcontainer = await this.#webcontainer;

    try {
      await webcontainer.internal.textSearch(
        searchQuery,
        {
          isRegex,
          caseSensitive,
          gitignore: true,
          requireGit: true,
          excludes: this.#excludedFromSearch,
          folders: [WORK_DIR],
        },
        (path, matches) => {
          this.isSearching.set(true);
          this.results.setKey(path, matches);
        },
      );
    } catch (error) {
      this.searchError.set('Something went wrong. Please try again.');
      logger.error(`Failed to perform search:\n\n${error}`);
    } finally {
      this.isSearching.set(false);
    }
  }

  selectSearchResult(path: string) {
    const [filePath, lineNumber = 1] = path.split(':');

    workbenchStore.setSelectedFileProgrammatically(filePath);
    workbenchStore.setActiveLineNumber(Number(lineNumber));
  }
}
